import {
  Text,
  HStack,
  Stack,
  useTheme,
  IconButton,
  Icon,
  Input,
  VStack,
} from '@wegroup/design-system';
import { trimStart } from 'lodash';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce/lib';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { Inputs } from '../../../../types/Enums';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import FlowsBoxQuestionDescription from '../../../ui/advisory-flow/components/FlowsQuestionDescription';
import FlowsQuestionText from '../../../ui/advisory-flow/components/FlowsQuestionText';

interface Props {
  question: FlowsQuestion;
}

const AmountInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { colors } = useTheme();

  const {
    field: { onBlur, onChange, value },
    error,
  } = useFlowInputComponent({ question });

  const [debounceOnBlur] = useDebouncedCallback(onBlur, 500);

  const isValueEmpty = (value?: string) => value === '' || value == null;

  const handleOnButtonBlur = (value: number | undefined) => {
    onChange({ name: question.id, value });
    debounceOnBlur({ name: question.id, value });
  };

  const handleOnMinus = () => {
    if (isValueEmpty(value)) handleOnButtonBlur(0);
    if (value > 0) handleOnButtonBlur((value || 0) - 1);
  };

  const handleOnPlus = () => {
    handleOnButtonBlur((value || 0) + 1);
  };

  const handleOnValueChange = (value: string) => {
    return value === '' || value == null ? '' : parseInt(value);
  };

  const handleOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmedValue = trimStart(e.target.value, '0');
    const parsedValue = parseInt(trimmedValue);

    let value;
    if (e.target.value === '') value = '';
    else if (parsedValue >= 0) value = parsedValue;
    else value = 0;

    onBlur({
      name: question.id,
      value: value,
    });
  };

  const [debounceHandleBlur] = useDebouncedCallback(handleOnBlur, 500);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      name: question.id,
      value: handleOnValueChange(e.target.value),
    });
    debounceHandleBlur(e);
  };

  const handleOnFocus = () => {
    if (inputRef.current) {
      inputRef.current.type = 'text';
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
      inputRef.current.type = 'number';
    }
  };

  return (
    <VStack align="stretch">
      <Stack
        direction={{ base: 'column', tablet: 'row' }}
        align="center"
        w="100%"
        bg="white"
        px="8"
        py="4"
        spacing="6"
        border="1px"
        borderColor={error ? colors.danger.main : 'gray.50'}
        borderRadius="6px"
        transitionDuration="0.2s"
        boxShadow="base"
        justify="space-between"
      >
        <VStack align="stretch">
          <FlowsQuestionText question={question} />

          {question.description && (
            <FlowsBoxQuestionDescription question={question} />
          )}
        </VStack>
        {/* Opacity needed here since it is not suported on iconbuttons */}
        <HStack
          spacing="4"
          maxW="48"
          opacity={question.rules.is_disabled ? '0.4' : '1'}
        >
          <IconButton
            name={question.id}
            variant="grayed"
            icon={<Icon name="GeneralActionsMinusOrRemove" color="gray.300" />}
            aria-label="Remove amount"
            borderRadius="full"
            onClick={handleOnMinus}
            isDisabled={question.rules.is_disabled}
          />
          <Input
            name={question.id}
            ref={inputRef}
            value={value}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            textAlign="center"
            type="number"
            w="20"
            isDisabled={question.rules.is_disabled}
            data-test-id={getFlowsDataTestId(Inputs.AMOUNT, question.id)}
            // Needed since opacity does not work on the buttons themselves
            sx={{
              opacity: '1 !important',
            }}
          />
          <IconButton
            name={question.id}
            variant="secondary"
            icon={<Icon name="GeneralActionsAdd" color="primary.500" />}
            aria-label="Add amount"
            borderRadius="full"
            onClick={handleOnPlus}
            isDisabled={question.rules.is_disabled}
          />
        </HStack>
      </Stack>
      {error?.message && (
        <Text textAlign="right" color="danger.main">
          {t(error.message)}
        </Text>
      )}
    </VStack>
  );
};

export default AmountInputComponent;
